import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, MessageCircle, Loader2, ExternalLink, CalendarCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { PROFILE } from "../data/content";

// Web3Forms — simple form-to-email service (free tier 250 envios/mês).
// Pegue sua chave grátis em https://web3forms.com (insira o e-mail nivaldapj@hotmail.com)
// e cole abaixo OU defina REACT_APP_WEB3FORMS_KEY no arquivo .env antes do build.
const WEB3FORMS_KEY =
  process.env.REACT_APP_WEB3FORMS_KEY || "COLE_SUA_CHAVE_WEB3FORMS_AQUI";

const SERVICES = [
  "Psicoterapia Individual",
  "Terapia de Casal",
  "Terapia Familiar",
  "Avaliação Neuropsicológica",
  "Terapia Sexual / Sexologia",
  "Orientação Vocacional",
  "Transtorno Alimentar",
  "Primeira consulta",
  "Outro",
];

const initial = {
  name: "",
  email: "",
  phone: "",
  service_type: "",
  preferred_date: "",
  message: "",
};

export const Contato = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type) {
      toast.error("Preencha os campos obrigatórios.");
      return;
    }
    if (!WEB3FORMS_KEY || WEB3FORMS_KEY === "COLE_SUA_CHAVE_WEB3FORMS_AQUI") {
      toast.error(
        "Formulário ainda não configurado. Por favor entre em contato pelo WhatsApp."
      );
      return;
    }
    setLoading(true);
    try {
      const payload = {
        access_key: WEB3FORMS_KEY,
        from_name: "Site Drª Nivalda",
        subject: `Novo agendamento — ${form.name} (${form.service_type})`,
        replyto: form.email,
        Nome: form.name,
        Email: form.email,
        Telefone: form.phone,
        "Tipo de atendimento": form.service_type,
        "Data preferencial": form.preferred_date || "—",
        Mensagem: form.message || "—",
      };
      const r = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const result = await r.json();
      if (result.success) {
        toast.success(
          "Mensagem enviada com sucesso. A Dra. Nivalda entrará em contato em breve."
        );
        setForm(initial);
      } else {
        toast.error(result.message || "Não foi possível enviar agora. Tente novamente.");
      }
    } catch (err) {
      toast.error("Falha de conexão. Tente novamente em instantes.");
    } finally {
      setLoading(false);
    }
  };

  const waLink = `https://wa.me/${PROFILE.whatsapp_raw}?text=${encodeURIComponent(
    "Olá, Dra. Nivalda. Gostaria de agendar uma consulta."
  )}`;

  return (
    <section
      id="contato"
      data-testid="contact-section"
      className="py-20 md:py-28 bg-background"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.2em] uppercase text-royal-800 font-semibold">
            Agendamento
          </div>
          <h2 className="mt-3 font-serif-display text-3xl md:text-5xl text-royal-900 leading-tight">
            Vamos conversar.
          </h2>
          <p className="mt-5 text-foreground/75 text-base leading-relaxed">
            Deixe seus dados e o tipo de atendimento desejado. A Dra. Nivalda ou sua
            secretária entrará em contato para confirmar dia e horário.
          </p>

          <div className="mt-8 space-y-5">
            <InfoRow
              icon={MapPin}
              title="Consultório"
              lines={[PROFILE.address_line1, PROFILE.address_line2]}
              testid="contact-address"
            />
            <InfoRow
              icon={Phone}
              title="Telefone & WhatsApp"
              lines={[`Fixo: ${PROFILE.phone_landline}`, `WhatsApp: ${PROFILE.whatsapp_display}`]}
              testid="contact-phones"
            />
            <InfoRow
              icon={Mail}
              title="E-mail"
              lines={[PROFILE.email]}
              testid="contact-email"
              link={`mailto:${PROFILE.email}`}
            />
            <InfoRow
              icon={Clock}
              title="Formatos de atendimento"
              lines={["Presencial em Santos", "Teleconsulta para todo o Brasil"]}
              testid="contact-formats"
            />
          </div>

          <Button
            asChild
            data-testid="contact-whatsapp-button"
            className="mt-8 bg-[#25D366] hover:bg-[#1fb457] text-white h-12 px-6 rounded-md"
          >
            <a href={waLink} target="_blank" rel="noreferrer">
              <MessageCircle className="mr-2" size={18} /> Falar direto no WhatsApp
            </a>
          </Button>

          <div className="mt-4 p-5 rounded-xl bg-gradient-to-br from-gold-50 to-gold-100 border-2 border-gold-500/40">
            <div className="flex items-start gap-3">
              <CalendarCheck size={22} className="text-gold-700 shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-royal-900">
                  Prefere agendar em tempo real?
                </h4>
                <p className="mt-1 text-sm text-foreground/75">
                  Veja horários disponíveis e pague online direto pela plataforma Doctoralia.
                </p>
                <Button
                  asChild
                  data-testid="contact-doctoralia-button"
                  className="mt-3 bg-royal-800 hover:bg-royal-700 text-white h-10 rounded-md"
                >
                  <a
                    href={PROFILE.doctoralia_booking}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Agendar pelo Doctoralia
                    <ExternalLink className="ml-2" size={14} />
                  </a>
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-10 rounded-xl overflow-hidden border border-border">
            <iframe
              data-testid="contact-map"
              title="Mapa do consultório"
              src={PROFILE.map_embed}
              className="w-full h-[260px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="p-6 md:p-10 bg-white rounded-2xl border border-border shadow-sm"
          >
            <h3 className="font-serif-display text-2xl md:text-3xl text-royal-900">
              Solicite seu agendamento
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Retornamos em até 24 horas úteis.
            </p>

            <div className="mt-8 grid md:grid-cols-2 gap-5">
              <Field label="Nome completo *">
                <Input
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Seu nome"
                  className="h-12"
                  required
                />
              </Field>
              <Field label="E-mail *">
                <Input
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="voce@exemplo.com"
                  className="h-12"
                  required
                />
              </Field>
              <Field label="Telefone / WhatsApp *">
                <Input
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="(13) 9 0000-0000"
                  className="h-12"
                  required
                />
              </Field>
              <Field label="Tipo de atendimento *">
                <Select
                  value={form.service_type}
                  onValueChange={(v) => update("service_type", v)}
                >
                  <SelectTrigger
                    data-testid="contact-select-service"
                    className="h-12"
                  >
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Data preferencial (opcional)" className="md:col-span-2">
                <Input
                  type="date"
                  data-testid="contact-input-date"
                  value={form.preferred_date}
                  onChange={(e) => update("preferred_date", e.target.value)}
                  className="h-12"
                />
              </Field>
              <Field label="Mensagem (opcional)" className="md:col-span-2">
                <Textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Conte brevemente o motivo do atendimento..."
                  rows={4}
                />
              </Field>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="mt-8 w-full md:w-auto bg-royal-800 hover:bg-royal-700 text-white h-12 px-8 rounded-md"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={18} /> Enviando...
                </>
              ) : (
                <>
                  <Send className="mr-2" size={18} /> Enviar solicitação
                </>
              )}
            </Button>

            <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
              Ao enviar, você autoriza o contato sobre agendamento. Suas informações são
              tratadas com sigilo profissional conforme o código de ética do CFP.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <Label className="text-xs tracking-wider uppercase text-muted-foreground font-semibold">
      {label}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const InfoRow = ({ icon: Icon, title, lines, testid, link }) => {
  const content = (
    <div className="flex gap-4" data-testid={testid}>
      <div className="w-10 h-10 shrink-0 rounded-lg bg-royal-800/10 text-royal-800 flex items-center justify-center">
        <Icon size={18} />
      </div>
      <div>
        <div className="text-xs tracking-widest uppercase text-royal-800 font-semibold">
          {title}
        </div>
        {lines.map((l, i) => (
          <div key={i} className="text-sm text-foreground/80 mt-0.5">
            {l}
          </div>
        ))}
      </div>
    </div>
  );
  return link ? (
    <a href={link} className="block hover:opacity-80 transition-opacity">
      {content}
    </a>
  ) : (
    content
  );
};
